import React from "react";
import { useTranslation } from "react-i18next";
import { LimitsStatus, LimitsStatusStructure } from "src/api";
import { formatNumberToLocale } from "src/utils";
import "./payment-limits.scss";

interface Props {
  limitsStatus: LimitsStatus | null;
  /** Wallet/notion currency the values are denominated in (e.g. USDT). */
  currency: string;
  /** Platform display precision for that currency (caps the decimals). */
  precision: number;
  isLoading?: boolean;
}

const PERIODS: { key: "daily" | "weekly" | "monthly"; labelKey: string }[] = [
  { key: "daily", labelKey: "PAYMENT_LIMITS.DAILY" },
  { key: "weekly", labelKey: "PAYMENT_LIMITS.WEEKLY" },
  { key: "monthly", labelKey: "PAYMENT_LIMITS.MONTHLY" },
];

/**
 * Static per-period used/limit summary, in the wallet currency. Always
 * expanded — no toggle. A period is shown only when a cap is configured;
 * the block hides entirely when nothing is set. No single "available"
 * figure is shown (it's ambiguous across periods); the per-period
 * used/limit rows carry the information.
 */
const PaymentLimits: React.FC<Props> = ({ limitsStatus, currency, precision, isLoading = false }) => {
  const { t } = useTranslation();
  const payments = limitsStatus?.payments;

  const rows = PERIODS.map(({ key, labelKey }) => {
    const bucket: LimitsStatusStructure | undefined = payments?.[key];
    if (!bucket || bucket.limit == null) return null;
    const used = bucket.filled ?? 0;
    return (
      <div className="payment-limits__row" key={key}>
        <span className="payment-limits__row-label">{t(labelKey)}</span>
        <span className="payment-limits__row-value">
          {`${formatNumberToLocale(used, precision)} / ${formatNumberToLocale(bucket.limit, precision)} ${currency}`}
        </span>
      </div>
    );
  }).filter(Boolean);

  if (!isLoading && rows.length === 0) return null;

  return (
    <div className="payment-limits">
      <div className="payment-limits__title">{t("PAYMENT_LIMITS.TITLE")}</div>
      {rows}
    </div>
  );
};

export default PaymentLimits;
